// src/services/api.schemas.ts
// ✅ Zod схемы для runtime валидации API responses

import { z } from 'zod';

// ========== БАЗОВЫЕ СХЕМЫ ==========

const RecommendProductSchema = z.object({
  product_id: z.string(),
  product_name: z.string(),
  product_url: z.string(),
  product_photo: z.string(),
  description: z.string(),
  recommend_text: z.string().nullable().optional(),
});

// ========== API RESPONSES ==========

/**
 * ✅ TelegramAuthResponse - используется при авторизации
 * ВАЖНО: Многие поля могут быть null после reset, используем .nullable()
 */
export const TelegramAuthResponseSchema = z.object({
  success: z.boolean(),
  token: z.string().min(10, 'Token должен быть минимум 10 символов'),
  user: z.object({
    id_telegram: z.number(),
    questionary_done: z.boolean(),
    // ✅ Поля которые обнуляются после reset - используем .nullable()
    first_name: z.string().nullable(),
    last_name: z.string().nullable().optional(),
    username: z.string().nullable().optional(),
    age: z.number().nullable().optional(),
    feedback: z.string().nullable().optional(),
    skin_type: z.string().nullable().optional(),
    is_sensitive: z.boolean().nullable().optional(),
    is_sick: z.boolean().nullable().optional(),
    use_retinol_before: z.boolean().nullable().optional(),
    count_weeks_used_retinol_before: z.number().nullable().optional(),
    frequency_weeks_used_retinol_before: z.number().nullable().optional(),
    last_use_retinol: z.string().nullable().optional(),
    plan_start_use_retinol: z.string().nullable().optional(),
    stage: z.number().nullable().optional(),
    need_notifications: z.boolean().nullable().optional(),
    language_code: z.string().nullable().optional(),
    is_premium: z.boolean().nullable().optional(),
    photo_url: z.string().nullable().optional(),
  }),
  dates: z.array(z.string()).optional(),
  recommend_products: z.array(RecommendProductSchema).optional(),
});

/**
 * ✅ QuestionnaireResponse - ответ после отправки анкеты
 */
export const QuestionnaireResponseSchema = z.object({
  success: z.boolean(),
  dates: z.array(z.string()).nullable().optional(),
  recommend_products: z.array(RecommendProductSchema).nullable().optional(),
});

/**
 * ✅ UserInfoResponse - используется в getUserInfo и syncWithBackend
 * ВАЖНО: Большинство полей nullable после reset
 */
export const UserInfoResponseSchema = z.object({
  id_telegram: z.number(),
  questionary_done: z.boolean(),
  real_name: z.string().nullable().optional(), // Бекенд отдает real_name
  first_name: z.string().nullable().optional(), // Legacy
  last_name: z.string().nullable().optional(),
  username: z.string().nullable().optional(),
  photo_url: z.string().nullable().optional(),
  age: z.number().nullable().optional(),
  skin_type: z.string().nullable().optional(),
  is_sensitive: z.boolean().nullable().optional(),
  is_sick: z.boolean().nullable().optional(),
  use_retinol_before: z.boolean().nullable().optional(),
  plan_start_use_retinol: z.string().nullable().optional(),
  need_notifications: z.boolean().nullable().optional(),
  dates: z.array(z.string()).nullable().optional(),
  recommend_products: z.array(RecommendProductSchema).nullable().optional(),
});

/**
 * ✅ SubmitFeedbackResponse - ответ после отправки feedback
 */
export const SubmitFeedbackResponseSchema = z.object({
  success: z.boolean(),
});

/**
 * ✅ UpdateNotificationsResponse - ответ после обновления уведомлений
 */
export const UpdateNotificationsResponseSchema = z.object({
  success: z.boolean(),
  error: z.string().nullable().optional(),
});

/**
 * ✅ UpdateTimezoneResponse - ответ после обновления timezone
 */
export const UpdateTimezoneResponseSchema = z.object({
  success: z.boolean(),
});

/**
 * ✅ ResetResponse - ответ после сброса данных
 */
export const ResetResponseSchema = z.object({
  success: z.boolean(),
});

// ========== ТИПЫ ДЛЯ ЭКСПОРТА ==========

export type TelegramAuthResponse = z.infer<typeof TelegramAuthResponseSchema>;
export type QuestionnaireResponse = z.infer<typeof QuestionnaireResponseSchema>;
export type UserInfoResponse = z.infer<typeof UserInfoResponseSchema>;
export type SubmitFeedbackResponse = z.infer<typeof SubmitFeedbackResponseSchema>;
export type UpdateNotificationsResponse = z.infer<typeof UpdateNotificationsResponseSchema>;
export type UpdateTimezoneResponse = z.infer<typeof UpdateTimezoneResponseSchema>;
export type ResetResponse = z.infer<typeof ResetResponseSchema>;
